import React, { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { RSWDLDebugger, type TEvent } from './debugger';

import type { TDevice } from '@/components/remote-software/native-web/useNativeDevice';
import type {
  BidirectionalOptions,
  TNativeToWebAction,
  TWebToNativeEvent,
  TWebToNativeRequest,
} from '@/components/remote-software/types/index.types';

import useHandshakePolling from '@/components/remote-software/hooks/useHandshakePolling';
import useNativeAction from '@/components/remote-software/hooks/useNativeAction';
import useNativeAuthBridge from '@/components/remote-software/native-web/native-to-web-handlers/useNativeAuthBridge';
import useNativeDevice from '@/components/remote-software/native-web/useNativeDevice';
import { languageKey, SHOW_RSWDL_DEBUGGER } from '@/config/EnviromentConfig';
import { setIsNativeContext } from '@/config/nativeTokenStore';

const showRSWDLDebugger = SHOW_RSWDL_DEBUGGER === 'true';

const notImplementedError = 'Not implemented';

type TEnvironmentContext = {
  device: TDevice;
  addRequest: (request: string) => void;
  addResponse: (response: string) => void;
  addLog: (log: string) => void;
  isNative: boolean;
  nativeAction: {
    sendRequestToNative: (
      actionName: TWebToNativeRequest,
      payload?: unknown,
      options?: BidirectionalOptions
    ) => Promise<unknown>;
    sendEventToNative: (
      actionName: TWebToNativeEvent,
      payload?: unknown,
      options?: BidirectionalOptions
    ) => Promise<boolean>;
    registerRequestHandler: (
      actionName: TNativeToWebAction,
      handler: (payload: unknown, requestId: string) => Promise<unknown> | unknown
    ) => () => void;
    registerEventListener: (actionName: TNativeToWebAction, listener: (payload: unknown) => void) => () => void;
    sendResponseToNative: (
      requestId: string,
      responseData: unknown,
      success: boolean,
      error?: string,
      requestName?: string,
      options?: BidirectionalOptions
    ) => Promise<boolean>;
    named: {
      events: {
        startSoftwareInstallation: (payload: object) => Promise<boolean>;
        showAcknowledgements: () => Promise<boolean>;
        sessionExpired: (payload?: object) => Promise<boolean>;
        sessionActive: (payload?: object) => Promise<boolean>;
        logout: () => Promise<boolean>;
        editProfile: () => Promise<boolean>;
      };
      requests: {
        handshake: (payload?: unknown, options?: BidirectionalOptions) => Promise<unknown>;
        refreshToken: (payload?: unknown, options?: BidirectionalOptions) => Promise<unknown>;
      };
    };
  };
};

const defaultEnvironment: TEnvironmentContext = {
  device: {
    model: '',
    platform: '',
    type: 'browser',
    userAgent: '',
  },
  addRequest: () => new Error(notImplementedError),
  addResponse: () => new Error(notImplementedError),
  addLog: () => new Error(notImplementedError),
  isNative: true,
  nativeAction: {
    sendRequestToNative: () => Promise.reject(new Error(notImplementedError)),
    sendEventToNative: () => {
      throw new Error(notImplementedError);
    },
    registerRequestHandler: () => () => {
      throw new Error(notImplementedError);
    },
    registerEventListener: () => () => {
      throw new Error(notImplementedError);
    },
    sendResponseToNative: () => {
      throw new Error(notImplementedError);
    },
    named: {
      events: {
        startSoftwareInstallation: () => Promise.reject(new Error(notImplementedError)),
        showAcknowledgements: () => Promise.reject(new Error(notImplementedError)),
        sessionExpired: () => Promise.reject(new Error(notImplementedError)),
        sessionActive: () => Promise.reject(new Error(notImplementedError)),
        logout: () => Promise.reject(new Error(notImplementedError)),
        editProfile: () => Promise.reject(new Error(notImplementedError)),
      },
      requests: {
        handshake: () => Promise.reject(new Error(notImplementedError)),
        refreshToken: () => Promise.reject(new Error(notImplementedError)),
      },
    },
  },
};

interface IEnvironmentContextProviderProps {
  children?: ReactNode;
}

export const EnvironmentContext = createContext(defaultEnvironment);

const EnvironmentContextProvider: React.FC<IEnvironmentContextProviderProps> = ({ children }) => {
  const [detectedNativeEnv, setDetectedNativeEnv] = useState(true);
  const [requests, setRequests] = useState<TEvent[]>([]);
  const [responses, setResponses] = useState<TEvent[]>([]);
  const [logs, setLogs] = useState<TEvent[]>([]);

  const addRequest = useCallback(
    (req: string) => setRequests((prev) => [...prev, { registeredAt: new Date(), message: req }]),
    []
  );
  const addLog = useCallback(
    (req: string) => setLogs((prev) => [...prev, { registeredAt: new Date(), message: req }]),
    []
  );
  const addResponse = useCallback((res: string) => {
    if (res.includes('react-devtools')) {
      return;
    }
    setResponses((prev) => [...prev, { registeredAt: new Date(), message: res }]);
  }, []);

  // Use the new handshake polling hook
  const {
    isPolling,
    completedHandshake,
    handshakeError: hasHandshakeError,
  } = useHandshakePolling({
    maxAttempts: 10,
    intervalMs: 1500,
    onTrigger: addRequest,
    onResponse: addResponse,
    onDetectedNativeEnvChange: setDetectedNativeEnv,
  });

  const isConnected = completedHandshake && !isPolling;
  const handshakeError = hasHandshakeError ? { registeredAt: new Date(), message: 'Handshake failed' } : undefined;

  // Use the new useNativeDevice hook
  const device = useNativeDevice();

  // Sync native context state to module-level store (for getToken() access)
  useEffect(() => {
    setIsNativeContext(completedHandshake);
  }, [completedHandshake]);

  // Initialize nativeAction hook once at provider level
  const nativeActionRaw = useNativeAction();

  // Activate native auth bridge for token handling
  useNativeAuthBridge(completedHandshake, nativeActionRaw);

  // Create nativeAction API with named methods
  const nativeAction = useMemo(() => {
    const {
      sendRequestToNative,
      sendEventToNative,
      registerRequestHandler,
      registerEventListener,
      sendResponseToNative,
    } = nativeActionRaw;

    return {
      // Core generic API (for advanced/dynamic use cases)
      sendRequestToNative,
      sendEventToNative,
      registerRequestHandler,
      registerEventListener,
      sendResponseToNative,

      // Named helpers under "named" namespace
      named: {
        events: {
          startSoftwareInstallation: (payload: object) => sendEventToNative('startSoftwareInstallation', payload),
          showAcknowledgements: () => sendEventToNative('showAcknowledgements'),
          sessionExpired: (payload?: object) => sendEventToNative('sessionExpired', payload),
          sessionActive: (payload?: object) => sendEventToNative('sessionActive', payload),
          logout: () => sendEventToNative('logout'),
          editProfile: () => sendEventToNative('editProfile'),
        },
        requests: {
          handshake: (payload?: unknown, options?: BidirectionalOptions) =>
            sendRequestToNative('handshake', payload, options),
          refreshToken: (payload?: unknown, options?: BidirectionalOptions) =>
            sendRequestToNative('refreshToken', payload, options),
        },
      },
    };
  }, [nativeActionRaw]);

  const environment = useMemo(
    () => ({
      device,
      addRequest,
      addResponse,
      addLog,
      isNative: completedHandshake,
      nativeAction,
    }),
    [device, addRequest, addResponse, addLog, completedHandshake, nativeAction]
  );

  return (
    <EnvironmentContext.Provider value={environment}>
      {children}
      {showRSWDLDebugger ? (
        <RSWDLDebugger
          isPolling={isPolling}
          isConnected={isConnected}
          detectedNativeEnv={detectedNativeEnv}
          hasError={handshakeError}
          requests={requests}
          responses={responses}
          logs={logs}
          languageReceived={localStorage.getItem(languageKey) ?? 'n/a'}
        />
      ) : null}
    </EnvironmentContext.Provider>
  );
};

export default EnvironmentContextProvider;
